// TASK-20260818 F1/F8 — the ONE predicate for tenant-scoped loads. Full incident history in
// docs/tasks/active/TASK-20260818-org-scoping-regression-local-e2e.md; the short version: the
// stale-response guard existed in ONE loader (orders) and not its siblings, so a slow response
// scoped to a superseded org (F1) or venue (F8) could land last and sit on screen as another
// tenant's/venue's data. Every tenant-scoped store writer routes through this predicate.

/** Dependencies for a tenant-scoped loader. */
export interface ScopedLoaderDeps<T> {
  /** Reads the CURRENT selected org. Called twice per run: capture at send, compare at land. */
  getOrgId: () => string | null | undefined;
  /**
   * Extra scope dimensions (venue id, strategy id, …) captured with the org and compared
   * element-wise when the response lands. Omit for org-only reads.
   */
  getScope?: () => readonly unknown[];
  /** Performs the scoped fetch. */
  load: (orgId: string) => Promise<T>;
  /** Commits a still-current result to the store. */
  apply: (data: T) => void;
  /** Handles a still-current failure. */
  onError?: (err: unknown) => void;
}

/**
 * Wraps a tenant-scoped load so every caller enforces the same two rules:
 *
 *  1. No resolved org → skip. A scope-less read is a guaranteed 400 (ADR-0023) — never burn the
 *     request or log a scary error during the startup window before the org context resolves.
 *  2. Capture-then-compare: the full scope (org + any extra dimensions) is captured at call time,
 *     and a response — success OR failure — arriving after any dimension moved on is dropped; it
 *     belongs to a superseded tenant view.
 */
export function createScopedLoader<T>({
  getOrgId,
  getScope = () => [],
  load,
  apply,
  onError = () => {},
}: ScopedLoaderDeps<T>): () => Promise<void> {
  const currentScope = (): readonly unknown[] => [getOrgId(), ...getScope()];

  return async function run(): Promise<void> {
    const requestedOrgId = getOrgId();
    if (!requestedOrgId) return;
    const requested = [requestedOrgId, ...getScope()];

    const superseded = () => {
      const now = currentScope();
      return requested.length !== now.length || requested.some((v, i) => v !== now[i]);
    };

    try {
      const data = await load(requestedOrgId);
      if (superseded()) return; // a stale success must not overwrite
      apply(data);
    } catch (err) {
      if (superseded()) return; // a stale failure must not blank newer data
      onError(err);
    }
  };
}
